import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { APP_NAME } from '../../constants';
import LoginForm from './components/LoginForm';
import TrustSignals from './components/TrustSignals';
import RoleCredentialsGuide from './components/RoleCredentialsGuide';
import BenefitsPanel from './components/BenefitsPanel';

const LoginAuthentication = () => {
  const [selectedRole, setSelectedRole] = useState(null);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated === 'true') {
      console.log('User already authenticated');
    }
  }, []);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  return (
    <>
      <Helmet>
        <title>Connexion - {APP_NAME} | Accès Sécurisé</title>
        <meta 
          name="description" 
          content={`Connectez-vous à votre espace ${APP_NAME}. Accès sécurisé pour clients, techniciens, dispatch et administrateurs. Conformité RGPD garantie.`} 
        />
      </Helmet>
      
      <div className="marketing-shell">
        {/* Header style portfolio */}
        <div className="marketing-header fixed top-0 left-0 right-0 z-50">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.location.href = '/'}>
              <div className="brand-mark w-10 h-10">
                <Icon name="Droplet" size={22} color="#FFFFFF" />
              </div>
              <span className="text-xl font-semibold text-white">
                {APP_NAME}
              </span>
            </div>
          </div>
        </div>

        <main className="pt-24 pb-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div className="space-y-6">
                <div className="text-center lg:text-left">
                  <p className="marketing-kicker mb-4">Espace sécurisé</p>
                  <h1 className="marketing-title text-3xl lg:text-5xl font-heading font-bold mb-3">
                    Bienvenue sur {APP_NAME}
                  </h1>
                  <p className="marketing-muted text-lg">
                    Votre plateforme de gestion de services de plomberie professionnelle
                  </p>
                </div>

                <div className="flex justify-center lg:justify-start">
                  <LoginForm onRoleSelect={handleRoleSelect} />
                </div>

                <div className="lg:hidden">
                  <RoleCredentialsGuide />
                </div>
              </div>

              <div className="space-y-6">
                <div className="hidden lg:block">
                  <RoleCredentialsGuide />
                </div>

                <div className="hidden lg:block">
                  <TrustSignals />
                </div>

                <div className="hidden xl:block">
                  <BenefitsPanel />
                </div>
              </div>
            </div>

            <div className="lg:hidden mt-8 space-y-6">
              <TrustSignals />
              <BenefitsPanel />
            </div>

            {/* Statistiques */}
            <div className="glass-panel mt-12 rounded-2xl p-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(59, 130, 246, 0.2)" }}>
                    <Icon name="Users" size={24} style={{ color: "#3b82f6" }} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1" style={{ color: "#3b82f6" }}>
                    15 000+
                  </h3>
                  <p className="text-sm text-gray-400">
                    Clients satisfaits
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(34, 197, 94, 0.2)" }}>
                    <Icon name="CheckCircle" size={24} style={{ color: "#22c55e" }} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1" style={{ color: "#22c55e" }}>
                    98%
                  </h3>
                  <p className="text-sm text-gray-400">
                    Taux de satisfaction
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(234, 179, 8, 0.2)" }}>
                    <Icon name="Clock" size={24} style={{ color: "#eab308" }} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1" style={{ color: "#eab308" }}>
                    &lt; 2h
                  </h3>
                  <p className="text-sm text-gray-400">
                    Temps d'intervention moyen
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer style portfolio */}
        <footer className="marketing-footer mt-12">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="brand-mark w-8 h-8">
                    <Icon name="Droplet" size={18} color="#FFFFFF" />
                  </div>
                  <span className="font-semibold text-white">{APP_NAME}</span>
                </div>
                <p className="text-sm text-gray-400">
                  Votre partenaire de confiance pour tous vos besoins en plomberie.
                </p>
                <br></br>
                <div className="flex flex-col gap-2">
                  <a href="/faq" className="text-blue-400 hover:underline">
                    FAQ
                  </a>
                  <a href="/cgv" className="text-blue-400 hover:underline">
                    CGV
                  </a>
                  <a href="/privacy-policy" className="text-blue-400 hover:underline">
                    Politique de Confidentialité
                  </a>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-white mb-3">Services</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li>Dépannage d'urgence</li>
                  <li>Installation sanitaire</li>
                  <li>Rénovation</li>
                  <li>Entretien préventif</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-white mb-3">Légal</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li>Mentions légales</li>
                  <li>Politique RGPD</li>
                  <li>
                    <a href="/cgv" className="text-blue-400 hover:underline">
                      CGV
                    </a>
                  </li>
                  <li>Cookies</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-white mb-3">Contact</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-center gap-2">
                    <Icon name="Phone" size={14} />
                    01 23 45 67 89
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Mail" size={14} />
                    contact@plumberpro.fr
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="MapPin" size={14} />
                    Paris, France
                  </li>
                </ul>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-500">
                © {new Date()?.getFullYear()} {APP_NAME}. Tous droits réservés.
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">Certifié:</span>
                <div className="flex gap-2">
                  <span className="px-2 py-1 rounded text-xs font-medium" style={{ background: "rgba(34, 197, 94, 0.15)", color: "#22c55e" }}>
                    RGPD
                  </span>
                  <span className="px-2 py-1 rounded text-xs font-medium" style={{ background: "rgba(34, 197, 94, 0.15)", color: "#22c55e" }}>
                    Qualibat
                  </span>
                  <span className="px-2 py-1 rounded text-xs font-medium" style={{ background: "rgba(34, 197, 94, 0.15)", color: "#22c55e" }}>
                    RGE
                  </span>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LoginAuthentication;